// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import WishlistLineNotes from "@bg/App/Components/MyListDetails/LineNotes";
import deleteLine from "@bg/App/Handlers/Pages/MyListDetails/DeleteWishlistLine";
import setGroupWishListLineIsSelected from "@bg/App/Handlers/Pages/MyListDetails/SetGroupSelectedLines";
import updateLineInfo from "@bg/App/Handlers/Pages/MyListDetails/UpdateLineInfo";
import { WishListGroupingItem } from "@bg/App/Services/WishListService";
import { CustomAppState } from "@bg/App/Store/Reducers";
import { printItems } from "@bg/App/Utilities/PrintItemsQrCode";
import ProductAddToCartButton from "@bg/Overrides/Components/ProductAddToCartButton";
import ProductContextAvailability from "@bg/Overrides/Components/ProductContextAvailability";
import ProductDescription from "@bg/Overrides/Components/ProductDescription";
import TwoButtonModal from "@bg/Overrides/Components/TwoButtonModal";
import { productListLineStyles } from "@bg/Overrides/Widgets/MyListsDetails/MyListDetailsProductListLineStyles";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import { getUnitRegularPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import {
    getWishListLinesDataView,
    isWishListLineDeactivated,
    isWishListLineDiscontinued,
    isWishListLineRestricted,
} from "@insite/client-framework/Store/Data/WishListLines/WishListLinesSelectors";
import updateUnitOfMeasure from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/UpdateUnitOfMeasure";
import translate from "@insite/client-framework/Translate";
import { ProductModel, WishListLineModel, WishListModel } from "@insite/client-framework/Types/ApiModels";
import LocalizedCurrency from "@insite/content-library/Components/LocalizedCurrency";
import ProductImage from "@insite/content-library/Components/ProductImage";
import ProductImageBadges from "@insite/content-library/Components/ProductImageBadges";
import ProductPartNumbers from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice from "@insite/content-library/Components/ProductPrice";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import ProductTextBadges from "@insite/content-library/Components/ProductTextBadges";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import Button, { ButtonIcon } from "@insite/mobius/Button";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox";
import Icon from "@insite/mobius/Icon";
import Link from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import cloneDeep from "lodash/cloneDeep";
import React, { useEffect, useState } from "react";
import { QRCode } from "react-qrcode-logo";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    wishList: WishListModel;
    wishListLine: WishListLineModel;
    groupName: string;
    qty: number;
    notes: string;
    isStaticList?: boolean;
    showImageBadges: boolean;
    maximumNumberOfImageBadges: number;
    showTextBadges: boolean;
    maximumNumberOfTextBadges: number;
    onViewReplacementProduct?: (wishListLine: WishListLineModel) => void;
    // XNG-Changes:
    originalQty: number;
}

const mapStateToProps = (state: CustomAppState, ownProps: OwnProps) => {
    const productInfo = ownProps.isStaticList
        ? state.pages.staticList.productInfosByWishListLineId
        : state.pages.myListDetails.productInfosByWishListLineId;
    return {
        settingsCollection: getSettingsCollection(state),
        selectedGroupLines: state.commonActions["selectedGroupLines"],
        productInfo: productInfo[ownProps?.wishListLine?.id],
        sortBy: state.pages.myListDetails.loadWishListLinesParameter.sort,
        // XNG-Changes:
        isWishListLoading: getWishListLinesDataView(state, state.pages.myListDetails.loadWishListLinesParameter)
            .isLoading,
        isCartLoading: getCurrentCartState(state).isLoading,
        isUpdatingCart: state.context.isUpdatingCart,
    };
};

const mapDispatchToProps = {
    updateUnitOfMeasure,
    setGroupWishListLineIsSelected,
    updateLineInfo,
    deleteLine,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export const styles = productListLineStyles;

const MyListsDetailsProductListLine: React.FC<Props> = ({
    wishList,
    wishListLine,
    productInfo,
    settingsCollection,
    updateUnitOfMeasure,
    qty,
    notes,
    groupName,
    setGroupWishListLineIsSelected,
    selectedGroupLines,
    sortBy,
    showImageBadges,
    showTextBadges,
    maximumNumberOfImageBadges,
    maximumNumberOfTextBadges,
    onViewReplacementProduct,
    updateLineInfo,
    deleteLine,
    // XNG-Changes
    isWishListLoading,
    isCartLoading,
    isUpdatingCart,
    originalQty,
}) => {
    const product = mapWishListLineToProduct(wishListLine);
    const [deleteLineModalIsOpen, setDeleteLineModalIsOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [lineGroups, setLineGroups] = useState<WishListGroupingItem[]>([]);
    const dispatch = useAppDispatch();

    useEffect(() => {
        if (wishListLine && wishListLine?.properties?.grouping) {
            setLineGroups(JSON.parse(wishListLine.properties.grouping));
        }
    }, [wishListLine]);

    const selectChangeHandler: CheckboxProps["onChange"] = (_, value) => {
        setGroupWishListLineIsSelected({
            wishListLineIds: [wishListLine.id],
            groupName,
        });
    };

    const deleteWishlistLine = () => {
        const lineGroup = lineGroups.filter(group => group.groupName.toLowerCase() === groupName.toLowerCase())[0];
        setIsDeleting(true);
        deleteLine({
            wishListId: wishList.id,
            groupLineId: lineGroup.groupLineId,
            reloadWishList: true,
            onComplete: result => {
                if (result.successful) {
                    setDeleteLineModalIsOpen(false);
                    setIsDeleting(false);
                    setGroupWishListLineIsSelected({
                        wishListLineIds: [wishListLine.id],
                        groupName,
                        clearOnly: true,
                    });
                }
            },
        });
    };

    const updateLineNotes = (notes: string) => {
        const lineGroup = lineGroups.filter(
            (group: any) => group.groupName.toLowerCase() === groupName.toLowerCase(),
        )[0];
        if (notes === lineGroup.notes) {
            return;
        }

        updateLineInfo({
            wishListId: wishList!.id,
            updateNote: true,
            reloadWishList: true,
            linesModel: [
                {
                    wishListLineId: wishListLine.id,
                    groupLineId: lineGroup.groupLineId,
                    notes,
                },
            ],
        });
    };

    const canEditWishList = (wishList.allowEdit || !wishList.isSharedList) && !wishList.isAutogenerated;

    const customProductInfo: ProductInfo = {
        ...productInfo!,
        // XNG-Changes
        qtyOrdered: qty > 0 ? qty : 1,
    };
    const onQtyOrderedChanged = (qty: number) => {
        const lineGroup = lineGroups.filter(group => group.groupName.toLowerCase() === groupName.toLowerCase())[0];
        dispatch({
            type: "Pages/xProductListDetails/UpdateProductsQty",
            lineModel: {
                wishListLineId: wishListLine.id,
                groupLineId: lineGroup.groupLineId,
                qty,
                originalQty: lineGroup.qtyOrdered,
            },
        });
    };

    const { productSettings } = settingsCollection;
    const productContext: ProductContextModel = {
        product,
        productInfo: customProductInfo,
        onQtyOrderedChanged,
        onUnitOfMeasureChanged: unitOfMeasure => {
            updateUnitOfMeasure({ wishListLineId: wishListLine.id, unitOfMeasure });
        },
    };

    const { imageBadges, textBadges } = useWidgetBadges({ productContext, showTextBadges, showImageBadges });

    const getItemTotal = () => {
        if (!productInfo || !productInfo.pricing) {
            return 0;
        }

        return (
            Math.round(
                getUnitRegularPrice(productInfo.pricing!, productInfo.qtyOrdered).price *
                    (originalQty > 0 ? originalQty : 1) *
                    100,
            ) / 100
        );
    };

    // print line qr code
    const printItem = () => {
        printItems(`#qrCode_${wishListLine.id} canvas`, wishListLine, [productInfo!], true);
    };

    if (
        isWishListLineRestricted(wishListLine, customProductInfo) ||
        isWishListLineDiscontinued(wishListLine, customProductInfo)
    ) {
        return (
            <StyledWrapper
                {...styles.lineInnerContainer}
                data-test-selector={`${wishListLine.productId}_${wishListLine.selectedUnitOfMeasure}`}
            >
                <StyledWrapper {...styles.leftSection}>
                    <StyledWrapper {...styles.imageGridItem}></StyledWrapper>
                    <StyledWrapper
                        {...styles.descriptionGridItem}
                        className="wishlist-restricted-line-description-text"
                    >
                        {wishListLine.shortDescription}
                        <StyledWrapper
                            {...styles.partNumbersGridItem}
                            className="wishlist-restricted-line-part-number-grid-item"
                        >
                            <ProductPartNumbers
                                productNumber={product.detail?.sku || product.productNumber}
                                customerProductNumber={product.customerProductNumber}
                                manufacturerItem={product.manufacturerItem}
                                extendedStyles={styles.productPartNumbersStyles}
                            />
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.rightSection}>
                    <Typography
                        {...styles.restrictedMessageText}
                        className="wishlist-restricted-line-message-text"
                        data-test-selector="restrictedMessage"
                    >
                        {isWishListLineRestricted(wishListLine, customProductInfo) &&
                            !isWishListLineDeactivated(wishListLine) && (
                                <>{siteMessage("Lists_Item_Not_Displayed_Due_To_Restrictions")}</>
                            )}
                        {isWishListLineDeactivated(wishListLine) && (
                            <>{siteMessage("Lists_Item_Not_Displayed_Due_To_Deactivation")}</>
                        )}
                        {isWishListLineDiscontinued(wishListLine, customProductInfo) && (
                            <>{siteMessage("Lists_Item_Not_Displayed_Due_To_Discontinued")}</>
                        )}
                    </Typography>
                </StyledWrapper>
                <StyledWrapper
                    css={css`
                        display: flex;
                        align-items: center;
                        justify-content: flex-end;
                        @media (max-width: 1300px) {
                            position: absolute;
                            top: 10px;
                            right: 10px;
                        }
                    `}
                >
                    {canEditWishList && (
                        <Button
                            {...styles.deleteLink}
                            onClick={() => setDeleteLineModalIsOpen(true)}
                            data-test-selector="removeRestricted"
                            className="wishlist-restricted-line-remove-link"
                        >
                            <ButtonIcon src="Delete" size={18} color="secondary" />
                        </Button>
                    )}
                </StyledWrapper>
                <TwoButtonModal
                    modalIsOpen={deleteLineModalIsOpen}
                    headlineText={translate("Delete List Item")}
                    messageText={`${translate("Are you sure you want to delete this item?")}`}
                    cancelButtonText={translate("Cancel")}
                    submitButtonText={translate("Delete")}
                    onCancel={() => setDeleteLineModalIsOpen(false)}
                    onSubmit={deleteWishlistLine}
                    submitTestSelector="submitDeleteListItem"
                    showSubmitBtnLoadingOn={isDeleting}
                />
            </StyledWrapper>
        );
    }

    return (
        <ProductContext.Provider value={productContext}>
            <StyledWrapper
                {...styles.lineInnerContainer}
                id={`contentItem_${wishListLine.id}`}
                className="wishlist-line-grid-container"
                data-test-selector={`${wishListLine.productId}_${wishListLine.selectedUnitOfMeasure}`}
            >
                <StyledWrapper {...styles.leftSection}>
                    <StyledWrapper {...styles.imageGridItem}>
                        <StyledWrapper {...styles.dragGrid}>
                            {canEditWishList && (
                                <Checkbox
                                    {...styles.selectedCheckbox}
                                    checked={
                                        selectedGroupLines?.[groupName] &&
                                        selectedGroupLines[groupName].indexOf(wishListLine.id) > -1
                                    }
                                    onChange={selectChangeHandler}
                                    hiddenIconText={translate("Select")}
                                    data-test-selector="myListDetails_selectRowCheckbox"
                                />
                            )}
                            {canEditWishList && sortBy === "SortOrder" && (
                                <StyledWrapper {...styles.dragIconWrapper}>
                                    <Icon {...styles.dragHandleIcon} className="drag-handle" />
                                </StyledWrapper>
                            )}
                        </StyledWrapper>

                        <ProductImage product={productContext} extendedStyles={styles.productImageStyles} />
                    </StyledWrapper>

                    <StyledWrapper {...styles.descriptionGridItem} className="wishlist-line-description-grid-item">
                        <ProductDescription
                            product={productContext}
                            lines={2}
                            lineHeight={22}
                            extendedStyles={styles.productDescriptionStyles}
                        />
                        <StyledWrapper
                            {...styles.partNumbersGridItem}
                            className="wishlist-line-product-part-number-grid-item"
                        >
                            <ProductPartNumbers
                                productNumber={product.properties?.sku || product.productNumber}
                                customerProductNumber={product.customerProductNumber}
                                manufacturerItem={product.manufacturerItem}
                                extendedStyles={styles.productPartNumbersStyles}
                            />
                        </StyledWrapper>
                        <StyledWrapper {...styles.availabilityGridItem}>
                            {productSettings.showInventoryAvailability && (
                                <ProductContextAvailability extendedStyles={styles.productAvailabilityStyles as any} />
                            )}
                            {canEditWishList && (
                                <Link {...styles.printQrCode} onClick={printItem}>
                                    {translate("Print QR Code")}
                                </Link>
                            )}
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>

                <StyledWrapper {...styles.rightSection}>
                    <StyledWrapper {...styles.rightSectionTop}>
                        <StyledWrapper {...styles.priceAndAvailabilityInnerContainer}>
                            <StyledWrapper {...styles.priceGridItem}>
                                {customProductInfo.pricing?.additionalResults?.["noPriceFound"]?.toLowerCase() !==
                                "true" ? (
                                    <ProductPrice
                                        product={productContext}
                                        showSavings={false}
                                        extendedStyles={styles.productPriceStyles as any}
                                    />
                                ) : (
                                    <Typography
                                        variant="h6"
                                        weight={fontConfig.weight.bold}
                                        css={css`
                                            margin-bottom: 0;
                                            width: 100%;
                                            text-align: center;
                                            @media (max-width: 1024px) {
                                                text-align: left;
                                            }
                                            legend {
                                                font-size: ${fontConfig.fontSize.legend}px;
                                                font-weight: ${fontConfig.weight.bold};
                                                text-transform: uppercase;
                                            }
                                        `}
                                    >
                                        <legend>{translate("Price/Unit")}</legend>
                                        N/A
                                    </Typography>
                                )}
                            </StyledWrapper>

                            <StyledWrapper {...styles.uomAndQuantityInnerContainer}>
                                {product.unitOfMeasures && product.unitOfMeasures.length > 1 && (
                                    <StyledWrapper {...styles.uomGridItem}>
                                        <ProductUnitOfMeasureSelect
                                            disabled={!canEditWishList}
                                            extendedStyles={styles.productUnitOfMeasureSelectStyles}
                                        />
                                    </StyledWrapper>
                                )}
                                <StyledWrapper {...styles.quantityGridItem}>
                                    <StyledWrapper
                                        css={css`
                                            display: none;
                                            text-align: center;
                                            @media print {
                                                display: block;
                                            }
                                        `}
                                    >
                                        <Typography variant="legend">{translate("QTY")}</Typography>
                                    </StyledWrapper>
                                    <ProductQuantityOrdered
                                        {...({ changeOnBlur: true, editable: canEditWishList } as any)}
                                        extendedStyles={styles.productQuantityOrderedStyles as any}
                                        // XNG-Changes
                                        editable={!(isWishListLoading || isUpdatingCart || isCartLoading)}
                                    />
                                </StyledWrapper>
                            </StyledWrapper>
                            <StyledWrapper {...styles.lineTotal}>
                                <Typography variant="legend">{translate("Total")}</Typography>
                                <Typography {...styles.priceTotal}>
                                    {customProductInfo.failedToLoadPricing ||
                                    customProductInfo.pricing?.additionalResults?.["noPriceFound"]?.toLowerCase() ===
                                        "true" ? (
                                        translate("N/A")
                                    ) : (
                                        <LocalizedCurrency amount={getItemTotal()} />
                                    )}
                                </Typography>
                            </StyledWrapper>
                        </StyledWrapper>
                        <StyledWrapper {...styles.buttonsGridItem}>
                            <ProductAddToCartButton
                                notes={notes}
                                iconOnly={false}
                                sizeVariant="medium"
                                extendedStyles={styles.addToCartButton}
                                data-test-selector="addToCart"
                            />

                            {canEditWishList && (
                                <Button
                                    {...styles.deleteLink}
                                    onClick={() => setDeleteLineModalIsOpen(true)}
                                    data-test-selector="deleteListItem"
                                >
                                    <ButtonIcon src="Delete" size={18} color="secondary" />
                                </Button>
                            )}
                        </StyledWrapper>
                    </StyledWrapper>
                    {customProductInfo.pricing?.additionalResults?.["noPriceFound"]?.toLowerCase() === "true" && (
                        <StyledWrapper
                            css={css`
                                @media (max-width: 481px) {
                                    margin-top: 8px;
                                }
                            `}
                        >
                            <Typography
                                color="danger"
                                size={fontConfig.fontSize.xSmall}
                                lineHeight={fontConfig.lineHeight.singleLineSmall}
                            >
                                {siteMessage("Custom_Price_FailedToLoad")}
                            </Typography>
                        </StyledWrapper>
                    )}

                    {canEditWishList && (
                        <StyledWrapper {...styles.notesGridItem} className="wishlist-line-notes-grid-item">
                            <WishlistLineNotes notes={notes} onNotesChange={updateLineNotes} />
                        </StyledWrapper>
                    )}
                    {showTextBadges && textBadges && (
                        <ProductTextBadges
                            badgeList={textBadges}
                            maxBadges={maximumNumberOfTextBadges ?? 4}
                            extendedStyles={styles.textBadgeStyles}
                        />
                    )}
                    {showImageBadges && imageBadges && (
                        <ProductImageBadges
                            badgeList={imageBadges}
                            maxBadges={maximumNumberOfImageBadges ?? 4}
                            extendedStyles={styles.imageBadgeStyles}
                            numberOfColumns={4}
                        />
                    )}
                </StyledWrapper>
                <StyledWrapper
                    css={css`
                        display: none;
                    `}
                    id={`qrCode_${wishListLine.id}`}
                >
                    <QRCode
                        value={`${window.location.origin}${wishListLine.productUri}`}
                        logoWidth={250}
                        logoHeight={250}
                    />
                </StyledWrapper>
            </StyledWrapper>

            <TwoButtonModal
                modalIsOpen={deleteLineModalIsOpen}
                headlineText={translate("Delete List Item")}
                messageText={`${translate("Are you sure you want to delete this item?")}`}
                cancelButtonText={translate("Cancel")}
                submitButtonText={translate("Delete")}
                onCancel={() => setDeleteLineModalIsOpen(false)}
                onSubmit={deleteWishlistLine}
                submitTestSelector="submitDeleteListItem"
                showSubmitBtnLoadingOn={isDeleting}
            />
        </ProductContext.Provider>
    );
};

const mapWishListLineToProduct = (wishListLine: WishListLineModel) => {
    const product = cloneDeep(wishListLine) as any as ProductModel;
    product.id = wishListLine.productId;
    product.productNumber = wishListLine.erpNumber;
    product.productTitle = wishListLine.shortDescription;
    product.customerProductNumber = wishListLine?.customerName;
    product.unitOfMeasures =
        wishListLine?.productUnitOfMeasures?.map(u => ({
            id: u.productUnitOfMeasureId,
            ...u,
        })) || null;

    return product;
};

export default connect(mapStateToProps, mapDispatchToProps)(MyListsDetailsProductListLine);
